import React, {useEffect, Fragment} from 'react';

import {Divider} from '@mui/material';
import {t} from 'i18next';

// eslint-disable-next-line import/no-cycle
import {ANALYTICS, EVENT_SOURCE, SUGGESTEDLIST_CHANNELS} from 'contexts/analytics-context';
import useAnalytics from 'contexts/analytics-context';
import {useAppSelector, useAppDispatch} from 'hooks';
import ReactNative from 'services/react-native';
import {
  bulkUpdateBasketItemsPreviewAction,
  setSelectedItemsAction,
  updatePreviewItemQuantityAction
} from 'store/actions/basket-action';
import {AlcoTypeMapper, AnalyticsData, ProductSectionTypes, ProductType} from 'types';
import {ProductSectionProps} from 'types';
import {findBasketItemByVariantId, getIdsFromBasketItems, roundOffDecimalQuantity} from 'utils';
import {filter1PItems, filter3PItems, isSyncBasketEnabled} from 'utils/helper';
import {getProductAvailability, getProductIdWithoutPrefix} from 'utils/product';
import {SectionHeaderV2} from 'views/basket-header/SectionHeaderV2';
import {BasketProduct} from 'views/basket-product/Product';

import {BasketProductWrapper, StyledSectionContentContainer, StyledSectionWrap} from './styles';
import {MAKNET} from '../../constants/index';

export type BasketProps = {
  open: boolean;
  toggleBasketDrawer: () => void;
};

export const ProductSection: React.FC<ProductSectionProps> = ({mode, data, ...section}) => {
  const dispatch = useAppDispatch();
  const analytics = useAnalytics();

  const {currentStore, defaultMembershipCard} = useAppSelector((state) => state?.app);
  const {basket} = useAppSelector((state) => state?.basket);
  const idToken = useAppSelector((state) => state?.auth?.idToken);
  const currentStoreCodes = currentStore?.storeCodes;
  const [basketItem] = data;
  const shopId = basketItem?.productDetails?.sellerDetails?.shopId;
  const sellerType = basketItem?.productDetails?.sellerDetails?.fulfilledByPlatform;
  const shippingInfo = basketItem?.productDetails?.shippingInfo && basketItem?.productDetails?.shippingInfo[0];
  const productType =
    sellerType?.toLowerCase() === MAKNET.toLowerCase() ? ProductType.PRODUCT_1P : ProductType.PRODUCT_3P;

  const isReactNativeView = ReactNative.isReactNativeWebView();

  const isSyncBasket = isSyncBasketEnabled();
  const storeSelecteditems = useAppSelector((state) => state?.basket?.selectedItems);
  const localeSelectedItems = JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
  const selectedItems = isSyncBasket ? storeSelecteditems : localeSelectedItems;

  const isAlcoUnavailableSection = Object.values(AlcoTypeMapper).includes(section?.type as ProductSectionTypes);

  // NOTE: shitty solution, should be resolved with revamping the way we're storing selectedItems
  useEffect(() => {
    if (isAlcoUnavailableSection) {
      const isUnavailableSelected = data.some((item) => selectedItems.includes(item.productId));
      if (isUnavailableSelected) {
        const sectionProductIds = getIdsFromBasketItems(data);
        const filteredItems = selectedItems.filter((item: string) => !sectionProductIds.includes(item));
        dispatch(setSelectedItemsAction(filteredItems));
        !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify(filteredItems));
      }
    }
    // eslint-disable-next-line
  }, [data, dispatch, isAlcoUnavailableSection]);

  const updateItemQuantity = async (
    quantity: number,
    variantId: string,
    enablePartialQuantity?: boolean,
    analyticsData?: AnalyticsData
  ): Promise<void> => {
    const basketItem = findBasketItemByVariantId(data, variantId);
    try {
      dispatch(
        updatePreviewItemQuantityAction({
          productId: getProductIdWithoutPrefix(basketItem?.productId || ''),
          quantity: enablePartialQuantity ? quantity : roundOffDecimalQuantity(quantity),
          idToken,
          variantId
        })
      );
      if (analyticsData) {
        analytics.trackEvent(ANALYTICS.UPDATE_BASKET_ITEM, {
          sourceEvent: analyticsData.sourceEvent,
          triggerType: analyticsData.triggerType,
          basketId: basket?.id || 0,
          itemsCount: basket?.basketItems.length || 0,
          productId: analyticsData.productId || '',
          qty: analyticsData.qty,
          productPrice: analyticsData.productPrice || '',
          priceBeforeDiscount: basket?.prices?.totalOriginalPrice || 0,
          total: basket?.prices?.totalFinalPriceToPay || 0,
          discount: basket?.prices?.roundedDiscountAmount || 0,
          delivery: basket?.prices?.shippingFee || 0,
          storeId: basket?.storeCode || '',
          channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
          segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
          basketSize: basket?.basketItems?.length || 0,
          onePCount: filter1PItems(basket?.basketItems || []).length || 0,
          threePCount: filter3PItems(basket?.basketItems || []).length || 0
        });
      }
    } catch (error: any) {
      throw new Error(error.description);
    }
  };

  const deleteUnavailableItems = () => {
    try {
      const payload = data.map((itm) => ({
        quantity: 0,
        productId: itm.productId
      }));
      dispatch(bulkUpdateBasketItemsPreviewAction(payload));
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <StyledSectionWrap isReactNativeView={isReactNativeView} className={`${mode} ${'v2'}`}>
      <StyledSectionContentContainer>
        <SectionHeaderV2
          shopId={shopId}
          {...section}
          shippingInfo={shippingInfo}
          data={data}
          deleteUnavailableItems={deleteUnavailableItems}
        />

        {data.map((itm, key) => {
          let originPrice = itm.productDetails.originPrice;
          let displayPrice = itm.productDetails.displayPrice;
          const [variant] = itm.productDetails.variants;
          originPrice = parseFloat(variant.compareAtPrice);
          if (itm.promotedPrice) {
            displayPrice = itm.promotedPrice;
          } else {
            displayPrice = variant.price;
          }

          const {status: productStatus, isAvailable} = getProductAvailability(
            itm.productDetails,
            currentStoreCodes || [],
            {
              alcoholExpiryDate: defaultMembershipCard?.alcoholExpiryDate
            }
          );
          const isProductOnProhibitedPeriod = section.type === ProductSectionTypes.ALCOHOL_PROHIBITED_PERIOD;
          const showKg = itm?.productDetails?.isWeightedFresh;
          const enablePartialQuantity =
            showKg && itm?.productDetails?.moq === 1 && itm?.productDetails?.sellInPiece === false;
          return (
            <Fragment key={itm.id}>
              <BasketProductWrapper isReactNativeView={isReactNativeView} key={itm.id}>
                <BasketProduct
                  product={itm?.productDetails}
                  loading={false}
                  index={key}
                  isProductUnavailable={!isAvailable || isProductOnProhibitedPeriod}
                  productUnavailableText={
                    isProductOnProhibitedPeriod ? t(`label.restricted`) : t(`label.${productStatus}`)
                  }
                  title={itm.productDetails.title}
                  coverImageURL={itm.productDetails.imageUrls[0]}
                  onQuantityChange={(qty: number, analyticsData?: AnalyticsData) =>
                    updateItemQuantity(qty, itm?.variantId || '', enablePartialQuantity, analyticsData)
                  }
                  originPrice={originPrice}
                  displayPrice={displayPrice}
                  priceUnit={itm.productDetails.priceUnit}
                  quantity={itm.quantity}
                  weight={itm.productDetails?.size || ''}
                  moq={itm.productDetails?.moq || 1}
                  productId={itm.productId}
                  variantId={itm.variantId}
                  basketItem={itm}
                  upchargePercentage={itm.productDetails.upchargePercentage ?? 0}
                  upchargeMinimumThreshold={itm.productDetails.upchargeMinimumThreshold ?? 0}
                  maxQuantity={itm.productDetails.totalInventory}
                  promotedPrice={itm.productDetails.promotedPrice}
                  productType={productType}
                  productCappedLimit={itm.productDetails.productCappedLimit || 0}
                  remainingQuantity={itm?.productDetails?.remainingQuantity || 0}
                  isQuantityAdjusted={!!itm?.productDetails?.isQuantityAdjusted}
                  isAlcoUnavailableSection={isAlcoUnavailableSection}
                  displayPriceLineTotal={itm.productDetails.displayPriceLineTotal || displayPrice}
                  promotedPriceLineTotal={itm.productDetails.promotedPriceLineTotal || displayPrice}
                  originPriceLineTotal={itm.productDetails.originPriceLineTotal || originPrice}
                />
              </BasketProductWrapper>
              {data.length - 1 === key ? null : <Divider style={{borderColor: '#e8e8e8'}} />}
            </Fragment>
          );
        })}
      </StyledSectionContentContainer>
    </StyledSectionWrap>
  );
};

ProductSection.displayName = 'ProductSection';
